import { Dispatch } from '@reduxjs/toolkit';
import { runEmailCheckWorkflow, EmailCheckResult } from './emailWorkflowService';
import { Order } from '../types/order';
import { Client } from '../types/client';
import { addToast } from '../store/slices/toastsSlice';

interface SchedulerConfig {
  enabled: boolean;
  checkTimes: string[]; // Array of times in "HH:MM" format (24-hour)
  lastCheckTimestamp?: number;
}

/**
 * Email Scheduler Service
 * Manages automatic email checking on a schedule (twice daily by default)
 */
class EmailSchedulerService {
  private intervalId: NodeJS.Timeout | null = null;
  private config: SchedulerConfig = {
    enabled: true,
    checkTimes: ['09:00', '17:00'], // 9 AM and 5 PM by default
    lastCheckTimestamp: undefined
  };
  private dispatch: Dispatch | null = null;
  private getState:
    | (() => {
        orders: Order[];
        clients: Client[];
        recipes: { id: string; name: string }[];
      })
    | null = null;

  /**
   * Initialize the scheduler with Redux dispatch and state getter
   */
  initialize(
    dispatch: Dispatch,
    getState: () => {
      orders: Order[];
      clients: Client[];
      recipes: { id: string; name: string }[];
    }
  ) {
    this.dispatch = dispatch;
    this.getState = getState;

    // Load config from localStorage
    this.loadConfig();

    // Start the scheduler if enabled
    if (this.config.enabled) {
      this.start();
    }

    console.log('📅 Email scheduler initialized', this.config);
  }

  /**
   * Start the scheduler (checks every minute if it's time to run)
   */
  start() {
    if (this.intervalId) {
      console.warn('Scheduler already running');
      return;
    }

    // Check every minute if we need to run
    this.intervalId = setInterval(() => {
      this.checkIfTimeToRun();
    }, 60000); // Check every 60 seconds

    console.log('✅ Email scheduler started');
  }

  /**
   * Stop the scheduler
   */
  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
      console.log('⏹️  Email scheduler stopped');
    }
  }

  /**
   * Check if it's time to run based on configured times
   */
  private checkIfTimeToRun() {
    const now = new Date();
    const currentTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;

    // Check if current time matches any configured check time
    if (this.config.checkTimes.includes(currentTime)) {
      // Check if we already ran in the last 2 minutes (to avoid duplicate runs)
      const lastCheck = this.config.lastCheckTimestamp || 0;
      const timeSinceLastCheck = Date.now() - lastCheck;

      if (timeSinceLastCheck > 120000) {
        // More than 2 minutes since last check
        console.log(`⏰ Scheduled email check triggered at ${currentTime}`);
        this.runScheduledCheck();
      }
    }
  }

  /**
   * Run a scheduled email check
   */
  private async runScheduledCheck() {
    if (!this.dispatch || !this.getState) {
      console.error('Scheduler not properly initialized');
      return;
    }

    // Update last check timestamp
    this.config.lastCheckTimestamp = Date.now();
    this.saveConfig();

    try {
      const state = this.getState();
      const result = await runEmailCheckWorkflow(this.dispatch, state.orders, state.clients, state.recipes);

      // Show toast notification with results
      this.showResultToast(result);
    } catch (error) {
      console.error('Scheduled email check failed:', error);
      this.dispatch(addToast('Scheduled email check failed. Please try manually.', 'error'));
    }
  }

  /**
   * Manually trigger an email check (called from UI button)
   */
  async runManualCheck(): Promise<EmailCheckResult> {
    if (!this.dispatch || !this.getState) {
      throw new Error('Scheduler not properly initialized');
    }

    console.log('🔄 Manual email check triggered');

    const state = this.getState();
    const result = await runEmailCheckWorkflow(this.dispatch, state.orders, state.clients, state.recipes);

    // Show toast notification with results
    this.showResultToast(result);

    return result;
  }

  /**
   * Show toast notification based on email check results
   */
  private showResultToast(result: EmailCheckResult) {
    if (!this.dispatch) return;

    if (!result.success) {
      this.dispatch(addToast(`Email check failed: ${result.errorMessage || 'Unknown error'}`, 'error', 5000));
      return;
    }

    if (result.totalEmails === 0) {
      this.dispatch(addToast('No new order emails found', 'info', 3000));
      return;
    }

    // Success with results
    const parts: string[] = [];

    if (result.ordersCreated > 0) {
      parts.push(`${result.ordersCreated} order${result.ordersCreated !== 1 ? 's' : ''} created`);
    }

    if (result.ordersFlaggedForReview > 0) {
      parts.push(`${result.ordersFlaggedForReview} flagged for review`);
    }

    const message = parts.length > 0 ? parts.join(', ') : 'Email check completed';

    this.dispatch(addToast(`Email check: ${message}`, result.ordersFlaggedForReview > 0 ? 'warning' : 'success', 5000));
  }

  /**
   * Update scheduler configuration
   */
  updateConfig(config: Partial<SchedulerConfig>) {
    this.config = { ...this.config, ...config };
    this.saveConfig();

    // Restart scheduler if enabled state changed
    if (config.enabled !== undefined) {
      if (config.enabled) {
        this.start();
      } else {
        this.stop();
      }
    }

    console.log('📅 Scheduler config updated:', this.config);
  }

  /**
   * Get current configuration
   */
  getConfig(): SchedulerConfig {
    return { ...this.config };
  }

  /**
   * Save config to localStorage
   */
  private saveConfig() {
    try {
      localStorage.setItem('emailSchedulerConfig', JSON.stringify(this.config));
    } catch (error) {
      console.error('Failed to save scheduler config:', error);
    }
  }

  /**
   * Load config from localStorage
   */
  private loadConfig() {
    try {
      const saved = localStorage.getItem('emailSchedulerConfig');
      if (saved) {
        this.config = { ...this.config, ...JSON.parse(saved) };
      }
    } catch (error) {
      console.error('Failed to load scheduler config:', error);
    }
  }
}

// Export singleton instance
export const emailScheduler = new EmailSchedulerService();
